/**
 * 採点ロジック。フロントエンド(src/)とCloudflare Pages Functions(functions/)の
 * 両方から相対importで参照する単一の正とする（サーバー側の計算結果を保存時の正とする）。
 *
 * 満点は100点: 差し戻し点(30, 自動計算・評価者は編集不可) + コメント(25) +
 * ミス指摘(20) + 良かった点(10) + 構成(15) = 100。
 * 差し戻し点は `30 - 差し戻し基準×4`(scripts/evaluator_cli/return_log.py が
 * prepare-evaluation実行時に評価シートへ事前入力する)であり、マイナスにもなり得る。
 */

export const COMMENT_SCORE_OPTIONS = [0, 5, 10, 15, 20, 25] as const;
export const MISS_SCORE_OPTIONS = [0, 5, 10, 15, 20] as const;
export const GOOD_SCORE_OPTIONS = [0, 5, 10] as const;
export const STRUCTURE_SCORE_OPTIONS = [0, 5, 10, 15] as const;

// 差し戻し点は選択肢ではなく自動計算された連続値(マイナスもあり得る)。
// 評価者は編集できない(事前入力されたシートの値をそのまま使う)。
export const RETURN_SCORE_MAX = 30;
export const RETURN_SCORE_MULTIPLIER = 4;

export function calcReturnScore(returnRate: number): number {
  return RETURN_SCORE_MAX - returnRate * RETURN_SCORE_MULTIPLIER;
}

export type ScoreInput = {
  returnScore: number;
  commentScore: number;
  missScore: number;
  goodScore: number;
  structureScore: number;
};

// 評価者がプルダウンで選ぶ4項目のみ(差し戻し点は含まない)。
export type EditableScoreInput = Omit<ScoreInput, "returnScore">;

export type Rank = "S" | "A" | "B" | "C";

// 満点100点(差し戻し30 + 選択4項目70)に基づくランク基準。
export const RANK_THRESHOLDS: { rank: Rank; min: number }[] = [
  { rank: "S", min: 75 },
  { rank: "A", min: 60 },
  { rank: "B", min: 45 },
  { rank: "C", min: -Infinity },
];

export function isValidOption(value: number, options: readonly number[]): boolean {
  return options.includes(value);
}

export function validateEditableScoreInput(input: Partial<EditableScoreInput>): string | null {
  if (input.commentScore === undefined || !isValidOption(input.commentScore, COMMENT_SCORE_OPTIONS)) {
    return "コメント点が不正です";
  }
  if (input.missScore === undefined || !isValidOption(input.missScore, MISS_SCORE_OPTIONS)) {
    return "ミス点が不正です";
  }
  if (input.goodScore === undefined || !isValidOption(input.goodScore, GOOD_SCORE_OPTIONS)) {
    return "良かった点が不正です";
  }
  if (input.structureScore === undefined || !isValidOption(input.structureScore, STRUCTURE_SCORE_OPTIONS)) {
    return "構成点が不正です";
  }
  return null;
}

export function calcTotal(input: ScoreInput): number {
  return input.returnScore + input.commentScore + input.missScore + input.goodScore + input.structureScore;
}

export function calcRank(total: number): Rank {
  for (const { rank, min } of RANK_THRESHOLDS) {
    if (total >= min) return rank;
  }
  return "C";
}
